import {
  Check,
  CheckIcon,
  ChevronsUpDown,
  Component,
  Search,
  UsersRound,
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/DropdownMenu';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '~/components/ui/Sidebar';
import { useAuthStore } from '~/stores/useAuthStore';
import { useActiveTeam } from '~/stores/useTeamStore';
import { useState, useMemo } from 'react';
import { useToast } from '~/hooks/use-toast';
import { ScrollArea } from './ui/ScrollArea';
import { AddTeamDialog } from './AddTeamForm';
import { useTheme } from '~/contexts/ThemeContext';
import { Input } from './ui/Input';

export function TeamSwitcher() {
  const { isMobile } = useSidebar();
  const teams = useAuthStore((state) => state.teams);
  const switchTeam = useAuthStore((state) => state.switchTeam);
  const activeTeam = useActiveTeam();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { theme } = useTheme();

  const filteredTeams = useMemo(() => {
    if (!searchQuery.trim()) return teams;
    return teams.filter((team) =>
      team.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [teams, searchQuery]);
  const handleTeamSwitch = (teamId: number) => {
    try {
      switchTeam(teamId);
      setIsDropdownOpen(false);
      setSearchQuery('');

      toast({
        title: (
          <div className="flex items-center">
            <CheckIcon className="mr-2" />
            <span className="first-letter:capitalize">Success</span>
          </div>
        ),
        duration: 1500,
        variant: 'default',
        description: 'Team switched successfully!',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to switch team.',
        variant: 'destructive',
        duration: 3000,
      });
    }
  };

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
    if (!open) {
      setSearchQuery('');
    }
  };

  return (
    <SidebarMenuItem>
      <DropdownMenu
        open={isDropdownOpen}
        onOpenChange={handleDropdownOpenChange}
      >
        <DropdownMenuTrigger
          asChild
          className="border-none outline-none cursor-pointer"
        >
          <SidebarMenuButton
            size="lg"
            className="data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800"
          >
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground">
              <Component
                className="size-4"
                color={theme === 'dark' ? 'white' : 'black'}
              />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">
                {activeTeam && activeTeam.name}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-56 rounded-lg"
          align="start"
          side={isMobile ? 'bottom' : 'right'}
          sideOffset={4}
        >
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Teams
          </DropdownMenuLabel>
          {teams.length > 6 && (
            <div className="px-2 pb-2" onKeyDown={(e) => e.stopPropagation()}>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Search teams..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="pl-8 h-8 text-sm"
                  autoFocus
                />
              </div>
            </div>
          )}
          <ScrollArea
            className={`w-full ${teams.length > 5 ? 'h-60' : 'h-auto'}`}
          >
            {filteredTeams.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No teams found
              </div>
            ) : (
              filteredTeams.map((team) => (
                <DropdownMenuItem
                  key={team.id}
                  className="p-2 cursor-pointer"
                  onClick={() => handleTeamSwitch(team.id)}
                >
                  <div className="flex w-full gap-2 items-center">
                    <div className="flex size-6 shrink-0 items-center justify-center rounded-sm border">
                      <UsersRound className="size-4 shrink-0" />
                    </div>
                    <span className="text-nowrap">{team.name}</span>
                    {team.id === activeTeam?.id && (
                      <Check size={18} className="ml-2" />
                    )}
                  </div>
                </DropdownMenuItem>
              ))
            )}
          </ScrollArea>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className="cursor-pointer">
            <AddTeamDialog open={isDialogOpen} setOpen={setIsDialogOpen} />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
